import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Box, Typography, Divider, Tooltip, IconButton } from '@mui/material';

/** Material UI Icons. */
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { SelectField_single } from 'shared/ui/SelectForm';
import { AutocompleteFieldTradePartner } from 'shared/ui/AutoCompleteForm';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import { periodList } from 'shared/config/constantsData';
import validateFields from 'shared/lib/validateFields';
import { operationNow } from 'shared/lib/dateFormatter';

/** Initial filter state. */
const initFilter = {
    period: '',
    year: '',
    il_user_id: '',
};

/** Initial input validator. */
const initValidator = {
    il_user_id: false,
    period: false,
    year: false,
};

export default function EmployeeSetting(props) {
    /** Get props from Performance.js */
    const {
        setSearchLoading,
        employeeName,
        setCommonResult,
        setPersonalResult,
        setLaborChartData,
        setLaborChartLabel,
    } = props;

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Local state for current search condition. */
    const [filterCondition, setFilterCondition] = useState(initFilter);
    /** State to manage a validation when call to search. */
    const [validator, setValidator] = useState(initValidator);

    /** 연말에 한국에서 열어도 아직 오지 않은 해가 뜨지 않도록 근무지 달력으로 연도를 만든다. */
    const currentYear = operationNow().year();
    const yearOptions = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => currentYear - i);

    /** Handle select dropdown changes. */
    const handleSelectChange = (field) => (event) => {
        const selectedValue = event.target.value;

        setFilterCondition((prevState) => {
            const newState = {
                ...prevState,
                [field]: selectedValue,
            };

            // Reset year if selecting 'Yearly' period.
            if (field === 'period' && selectedValue === 'Yearly') {
                newState.year = '';
            }

            return newState;
        });
    };

    /** Handle autocomplete input changes */
    const handleAutocompleteChange = (key, value) => {
        setFilterCondition((prev) => ({ ...prev, [key]: value }));
    };

    /** Handle Search button click. */
    const handleSearch = () => {
        // Validate search condition before making API call.
        const validationRules = {
            period: () => !!filterCondition.period,
            year: () => {
                // Skip year validation if period is 'Yearly'.
                if (filterCondition.period === 'Yearly') return true;
                return !!filterCondition.year;
            },
            il_user_id: () => !!filterCondition.il_user_id,
        };

        const isValid = validateFields(validationRules, setValidator);

        if (!isValid) {
            dispatch(setSnackbar({ open: true, message: 'Please complete all required fields.', severity: 'warning' }));
            return;
        }
        callAPI({
            method: 'post',
            module: 'performance',
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(filterCondition),
            onInit: () => setSearchLoading(true),
            onSuccess: (resp) => {
                setCommonResult(resp.data.common_result_values);
                setPersonalResult(resp.data.personal_result_values);
                setLaborChartData(resp.data.score_by_date);
                setLaborChartLabel(resp.data.label);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong, please try again.',
                        severity: 'error',
                    })
                ),
            onFinal: () => {
                setSearchLoading(false);
            },
        });
    };

    /** Handle Reset button click. */
    const handleReset = () => {
        setCommonResult({});
        setPersonalResult({});
        setLaborChartData([]);
        setLaborChartLabel([]);
        setFilterCondition(initFilter);
        setValidator(initValidator);
    };

    return (
        <>
            <Box
                sx={{
                    borderRadius: '18px',
                    height: '20%',
                    boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.16)',
                    p: '20px',
                    mb: 2,
                }}
            >
                <Typography sx={{ color: '#003a52', fontSize: '15px', fontWeight: 500 }}>Employee Setting</Typography>
                <Divider sx={{ mt: 1, mb: 2 }} />
                {/* Filter Row 1: Period & Year. */}
                <Box
                    sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}
                >
                    <SelectField_single
                        label={'Period'}
                        options={periodList}
                        value={filterCondition.period}
                        onChange={handleSelectChange('period')}
                        sx={{ gap: 0 }}
                        labelStyle={{ width: '50px' }}
                        selectStyle={{ width: '100px' }}
                        error={validator.period}
                    />
                    <SelectField_single
                        label={'Year'}
                        options={yearOptions}
                        value={filterCondition.year}
                        onChange={handleSelectChange('year')}
                        sx={{ gap: 0 }}
                        labelStyle={{ width: '50px' }}
                        selectStyle={{ width: '100px' }}
                        disabled={filterCondition.period === 'Yearly'}
                        error={validator.year}
                    />
                </Box>
                {/* Filter Row 2: Employee Autocomplete & Action Buttons. */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '10px',
                        flexWrap: 'wrap',
                        mt: 1.5,
                    }}
                >
                    <AutocompleteFieldTradePartner
                        key={'il_user_id'}
                        label={'Name'}
                        name={'il_user_id'}
                        value={filterCondition.il_user_id}
                        onChange={(_, newValue) => handleAutocompleteChange('il_user_id', newValue)}
                        sx={{ gap: 0 }}
                        labelStyle={{ width: '50px' }}
                        AutocompleteStyle={{ width: '190px' }}
                        options={employeeName}
                        disable={true}
                        disableClearable={true}
                        error={validator.il_user_id}
                    />
                    <Tooltip title="Search">
                        <IconButton sx={{ color: 'white', p: 0.2, bgcolor: '#003a52' }} onClick={handleSearch}>
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear">
                        <IconButton sx={{ color: '#003a52', p: 0.2 }} onClick={handleReset}>
                            <RestartAltIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <CustomSnackbar />
            </Box>
        </>
    );
}
